import React, { useCallback, useEffect, useMemo } from 'react';

import { connect } from 'react-redux';

import { getQueryKey } from 'common/actions/ideaActivityQueries';
import ActivitySkeleton from 'common/subdomain/admin/v2/AdminV2IdeaSheet/ActivitySkeleton';
import ActivityList from 'common/subdomain/admin/v2/AdminV2IdeaSheet/InternalView/Activity/ActivityList';
import CommentComposer from 'common/subdomain/admin/v2/AdminV2IdeaSheet/InternalView/Activity/CommentComposer';
import { useIdeasContext } from 'common/subdomain/admin/v2/containers/IdeasContainer';
import Flex from 'common/uiV3/Flex';
import VStack from 'common/uiV3/VStack';

import type { Idea } from 'common/api/endpoints/idea';
import type { GetIdeaActivityResponse } from 'common/api/endpoints/ideaActivity';
import type {
  IdeaActivityQueriesState,
  IdeaActivityQueryState,
} from 'common/reducers/ideaActivityQueries';

type OwnProps = {
  idea: Idea;
};

type ConnectProps = {
  ideaActivityQuery: IdeaActivityQueryState;
};

type ActivityListProps = OwnProps & ConnectProps;

const ActivityTabContent = ({ idea, ideaActivityQuery }: ActivityListProps) => {
  const { loadIdeaActivity, loadMoreIdeaActivity, reloadIdeaActivityQuery } = useIdeasContext();

  // TODO: Add sort and limit to the query params when supported
  const activityQueryParams = useMemo(() => ({ ideaID: idea._id }), [idea._id]);

  useEffect(() => {
    loadIdeaActivity(activityQueryParams);
  }, [loadIdeaActivity, activityQueryParams]);

  useEffect(() => {
    if (ideaActivityQuery?.needsInvalidate) {
      reloadIdeaActivityQuery(ideaActivityQuery.queryParams);
    }
  }, [ideaActivityQuery?.needsInvalidate, reloadIdeaActivityQuery, ideaActivityQuery?.queryParams]);

  const handleLoadMore = useCallback(() => {
    if (!ideaActivityQuery || !ideaActivityQuery.hasNextPage) {
      return;
    }

    loadMoreIdeaActivity(ideaActivityQuery);
  }, [loadMoreIdeaActivity, ideaActivityQuery]);

  const ideaActivity: GetIdeaActivityResponse | undefined = useMemo(() => {
    if (!ideaActivityQuery?.items) {
      return undefined;
    }

    return {
      items: ideaActivityQuery.items,
      cursor: ideaActivityQuery.cursor ?? null,
      hasNextPage: ideaActivityQuery.hasNextPage ?? false,
    };
  }, [ideaActivityQuery?.items, ideaActivityQuery?.cursor, ideaActivityQuery?.hasNextPage]);

  const isLoading = ideaActivityQuery?.loading;
  const hasNextPage = ideaActivityQuery?.hasNextPage ?? false;
  const loadingMore = ideaActivityQuery?.loadingMore ?? false;

  return (
    <VStack className="flex-1">
      <Flex className="post flex-1 box-border !p-5">
        {isLoading ? (
          <ActivitySkeleton />
        ) : (
          <ActivityList
            idea={idea}
            ideaActivity={ideaActivity}
            activityQueryParams={activityQueryParams}
            hasNextPage={hasNextPage}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
          />
        )}
      </Flex>
      <div className="sticky bottom-0 z-2">
        <CommentComposer idea={idea} activityQueryParams={activityQueryParams} />
      </div>
    </VStack>
  );
};

export default connect(
  (
    { ideaActivityQueries }: { ideaActivityQueries: IdeaActivityQueriesState },
    ownProps: OwnProps,
  ) => {
    const queryKey = getQueryKey({ ideaID: ownProps.idea._id });
    return {
      ideaActivityQuery: ideaActivityQueries?.[queryKey],
    };
  },
)(ActivityTabContent) as unknown as React.FC<OwnProps>;
